/**
 * Empty State Component
 * Provides helpful, illustrated empty states instead of blank screens
 */

import React from 'react';
import Button from './Button';

interface EmptyStateProps {
  icon?: string | React.ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  illustration?: 'default' | 'search' | 'ideas' | 'plans' | 'charts' | 'templates';
  className?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  illustration = 'default',
  className = ''
}) => {
  // Illustration SVGs or emojis
  const illustrations = {
    default: '📋',
    search: '🔍',
    ideas: '💡',
    plans: '📝',
    charts: '📊',
    templates: '📄'
  };

  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}>
      {/* Icon/Illustration */}
      <div className="mb-6">
        {typeof icon === 'string' ? (
          <div className="text-7xl opacity-50">{icon || illustrations[illustration]}</div>
        ) : icon ? (
          <div className="text-gray-400">{icon}</div>
        ) : (
          <div className="text-7xl opacity-50">{illustrations[illustration]}</div>
        )}
      </div>

      {/* Title */}
      <h3 className="text-2xl font-bold text-gray-900 mb-3">
        {title}
      </h3>

      {/* Description */}
      {description && (
        <p className="text-gray-600 mb-8 max-w-md leading-relaxed">
          {description}
        </p>
      )}

      {/* Actions */}
      {(actionLabel || secondaryActionLabel) && (
        <div className="flex gap-3">
          {actionLabel && onAction && (
            <Button
              variant="primary"
              size="lg"
              onClick={onAction}
            >
              {actionLabel}
            </Button>
          )}
          {secondaryActionLabel && onSecondaryAction && (
            <Button
              variant="secondary"
              size="lg"
              onClick={onSecondaryAction}
            >
              {secondaryActionLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Preset empty states for common scenarios
export const EmptyBusinessPlans: React.FC<{ onCreate: () => void }> = ({ onCreate }) => (
  <EmptyState
    illustration="plans"
    title="No business plans yet"
    description="Create your first business plan in under 10 minutes with our AI-powered wizard"
    actionLabel="Create Your First Plan"
    onAction={onCreate}
  />
);

export const EmptyBusinessIdeas: React.FC<{ onGenerate: () => void }> = ({ onGenerate }) => (
  <EmptyState
    illustration="ideas"
    title="No business ideas yet"
    description="Use our AI to generate personalized business ideas based on your interests and skills"
    actionLabel="Generate Ideas"
    onAction={onGenerate}
  />
);

export const EmptySearchResults: React.FC<{ onClear?: () => void }> = ({ onClear }) => (
  <EmptyState
    illustration="search"
    title="No results found"
    description="Try adjusting your search terms or filters to find what you're looking for"
    actionLabel={onClear ? "Clear Search" : undefined}
    onAction={onClear}
  />
);

export const EmptyTemplates: React.FC = () => (
  <EmptyState
    illustration="templates"
    title="No templates match your criteria"
    description="Try selecting a different category or clearing your search filters"
  />
);

export const EmptyCharts: React.FC<{ onCreate: () => void }> = ({ onCreate }) => (
  <EmptyState
    illustration="charts"
    title="No financial data yet"
    description="Create your first financial forecast to see interactive charts and projections"
    actionLabel="Create Forecast"
    onAction={onCreate}
  />
);

export default EmptyState;
